import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Gauge, AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/web-vitals")({
  component: WebVitalsDashboard,
  head: () => ({ meta: [{ title: "Web Vitals — Platform Admin" }] }),
});

type Row = {
  id: string;
  created_at: string;
  metric_name: string;
  metric_value: number;
  rating: string | null;
  route: string | null;
  navigation_type: string | null;
  session_id: string | null;
};

const METRICS = ["LCP", "INP", "CLS", "FCP", "TTFB"] as const;
type Metric = (typeof METRICS)[number];

// Web Vitals "good" thresholds (Google).
const GOOD: Record<Metric, number> = { LCP: 2500, INP: 200, CLS: 0.1, FCP: 1800, TTFB: 800 };
const POOR: Record<Metric, number> = { LCP: 4000, INP: 500, CLS: 0.25, FCP: 3000, TTFB: 1800 };

function quantile(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

function fmt(metric: string, v: number): string {
  if (metric === "CLS") return v.toFixed(3);
  return `${Math.round(v)} ms`;
}

function ratingClass(metric: Metric, v: number): string {
  if (v <= GOOD[metric]) return "text-success";
  if (v <= POOR[metric]) return "text-warning";
  return "text-destructive";
}

function WebVitalsDashboard() {
  const { isPlatformAdmin } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);

  const load = async () => {
    setLoading(true);
    const since = new Date(Date.now() - hours * 3600 * 1000).toISOString();
    const { data, error } = await supabase
      .from("web_vitals" as never)
      .select("id, created_at, metric_name, metric_value, rating, route, navigation_type, session_id")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(5000);
    if (error) console.warn(error.message);
    setRows((data as Row[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [hours]);

  const summary = useMemo(() => {
    return METRICS.map((m) => {
      const vals = rows.filter((r) => r.metric_name === m).map((r) => Number(r.metric_value)).sort((a, b) => a - b);
      return {
        metric: m,
        count: vals.length,
        p50: quantile(vals, 0.5),
        p75: quantile(vals, 0.75),
        p95: quantile(vals, 0.95),
      };
    });
  }, [rows]);

  const byRoute = useMemo(() => {
    const map = new Map<string, { route: string; samples: number; lcp: number[]; inp: number[]; cls: number[] }>();
    for (const r of rows) {
      const k = r.route ?? "(unknown)";
      const e = map.get(k) ?? { route: k, samples: 0, lcp: [], inp: [], cls: [] };
      e.samples++;
      if (r.metric_name === "LCP") e.lcp.push(r.metric_value);
      if (r.metric_name === "INP") e.inp.push(r.metric_value);
      if (r.metric_name === "CLS") e.cls.push(r.metric_value);
      map.set(k, e);
    }
    return Array.from(map.values())
      .map((e) => ({
        route: e.route,
        samples: e.samples,
        lcp75: quantile(e.lcp.slice().sort((a, b) => a - b), 0.75),
        inp75: quantile(e.inp.slice().sort((a, b) => a - b), 0.75),
        cls75: quantile(e.cls.slice().sort((a, b) => a - b), 0.75),
      }))
      .sort((a, b) => b.samples - a.samples)
      .slice(0, 25);
  }, [rows]);

  if (!isPlatformAdmin) {
    return (
      <div className="px-3 py-6 md:px-8">
        <Card className="p-6 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-warning" />
          <h2 className="mt-2 font-semibold">Platform admins only</h2>
          <p className="mt-1 text-sm text-muted-foreground">You do not have access to this dashboard.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-3 py-4 md:px-8 md:py-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> Back to admin
          </Link>
          <h1 className="mt-1 flex items-center gap-2 font-display text-2xl font-bold md:text-3xl">
            <Gauge className="h-6 w-6 text-primary" /> Web Vitals
          </h1>
          <p className="text-sm text-muted-foreground">
            Real-user CLS, LCP, INP, FCP and TTFB collected from the browser.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm"
          >
            <option value={1}>Last 1h</option>
            <option value={24}>Last 24h</option>
            <option value={24 * 7}>Last 7d</option>
            <option value={24 * 30}>Last 30d</option>
          </select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        {summary.map((s) => (
          <Card key={s.metric} className="p-4">
            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{s.metric}</div>
            <div className={`mt-1 text-2xl font-bold ${s.count ? ratingClass(s.metric, s.p75) : ""}`}>
              {s.count ? fmt(s.metric, s.p75) : "—"}
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              p75 · {s.count.toLocaleString()} samples
            </div>
            {s.count > 0 && (
              <div className="mt-2 text-[11px] text-muted-foreground">
                p50 {fmt(s.metric, s.p50)} · p95 {fmt(s.metric, s.p95)}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Card className="mt-5 overflow-hidden">
        <div className="border-b border-border p-4">
          <h3 className="font-semibold">By route (p75)</h3>
          <p className="text-xs text-muted-foreground">Top {byRoute.length} routes by sample count.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3">Route</th>
                <th className="p-3">Samples</th>
                <th className="p-3">LCP</th>
                <th className="p-3">INP</th>
                <th className="p-3">CLS</th>
              </tr>
            </thead>
            <tbody>
              {byRoute.map((r) => (
                <tr key={r.route} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{r.route}</td>
                  <td className="p-3">{r.samples}</td>
                  <td className={`p-3 ${r.lcp75 ? ratingClass("LCP", r.lcp75) : ""}`}>{r.lcp75 ? fmt("LCP", r.lcp75) : "—"}</td>
                  <td className={`p-3 ${r.inp75 ? ratingClass("INP", r.inp75) : ""}`}>{r.inp75 ? fmt("INP", r.inp75) : "—"}</td>
                  <td className={`p-3 ${r.cls75 ? ratingClass("CLS", r.cls75) : ""}`}>{r.cls75 ? fmt("CLS", r.cls75) : "—"}</td>
                </tr>
              ))}
              {!loading && byRoute.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-sm text-muted-foreground">No samples yet. Visit a few pages to populate.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="mt-5 overflow-hidden">
        <div className="border-b border-border p-4">
          <h3 className="font-semibold">Recent samples</h3>
        </div>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3">Time</th>
                <th className="p-3">Metric</th>
                <th className="p-3">Value</th>
                <th className="p-3">Rating</th>
                <th className="p-3">Route</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 200).map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleTimeString()}</td>
                  <td className="p-3 font-medium">{r.metric_name}</td>
                  <td className="p-3">{fmt(r.metric_name, r.metric_value)}</td>
                  <td className="p-3 text-xs">
                    <span className={
                      r.rating === "good" ? "text-success" :
                      r.rating === "needs-improvement" ? "text-warning" :
                      r.rating === "poor" ? "text-destructive" : "text-muted-foreground"
                    }>{r.rating ?? "—"}</span>
                  </td>
                  <td className="p-3 font-mono text-xs">{r.route ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
